import React, { useState } from "react";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UserMinus, UserPlus } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ClientTherapistRelationship } from "@/types/therapist";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import ClientCardHeader from "./ClientCardHeader";
import ClientCardActions from "./ClientCardActions";
import DisconnectClientDialog from "./DisconnectClientDialog";
import { useQueryClient } from "@tanstack/react-query";
import ClientStatusBadge from "./ClientStatusBadge";

interface ClientCardProps {
  relationship: ClientTherapistRelationship;
  clientName: string;
  clientDetails: {
    company: string;
    fullName: string;
  };
  clientImage: string | null;
  initials: string;
  avatarColor: string;
  activityStatus: {
    status: "active" | "recent" | "inactive" | "unknown";
    text: string;
  };
  formatDate: (dateString: string) => string;
  onClientSelect: (clientId: string) => void;
  getClientDisplayName: (client?: ClientTherapistRelationship['client']) => string;
  isShared?: boolean;
  onRelationshipUpdate?: () => void;
}

const ClientCard = ({
  relationship,
  clientName,
  clientDetails,
  clientImage,
  initials,
  avatarColor,
  activityStatus,
  formatDate,
  onClientSelect,
  getClientDisplayName,
  isShared = false,
  onRelationshipUpdate
}: ClientCardProps) => {
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);

  const handleMakeInactive = async () => {
    try {
      const { error } = await supabase
        .from('client_therapist_relationships')
        .update({ status: 'inactive', disconnected_at: new Date().toISOString() })
        .eq('id', relationship.id);

      if (error) {
        toast.error("Failed to disconnect from client");
        return;
      }

      toast.success(`Disconnected from ${clientName}`);
      if (onRelationshipUpdate) {
        onRelationshipUpdate();
      }
    } catch (error) {
      console.error("Error updating relationship status:", error);
      toast.error("Failed to disconnect from client");
    }
  };

  const handleReactivate = async () => {
    let success = false;
    try {
      const { error } = await supabase
        .from('client_therapist_relationships')
        .update({ status: 'active', disconnected_at: null })
        .eq('id', relationship.id);

      if (error) {
        console.error("Supabase error updating relationship status:", error);
        return;
      }

      toast.success(`You are now re-connected with ${clientName}`);
      success = true;
      if (onRelationshipUpdate) {
        onRelationshipUpdate();
      }
      // Force a refetch of journal entries by invalidating the query
      const queryClient = useQueryClient();
      queryClient.invalidateQueries({ queryKey: ['client-journal-entries', relationship.client_id] });
    } catch (error) {
      if (!success) {
        console.error("Error updating relationship status:", error);
        toast.error("Failed to reconnect with client");
      }
    }
  };

  return (
    <Card key={relationship.id} className="hover:shadow-xl transition-all duration-300 bg-white backdrop-blur-sm border border-[#CCCCCC] transform hover:-translate-y-1">
      <CardHeader className="pb-2 bg-gradient-to-r from-[#E8F0F8] to-white rounded-t-lg">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
          <div className={relationship.status === 'inactive' ? 'opacity-50 grayscale hover:grayscale-0 hover:opacity-75' : ''} style={{ flex: 1 }}>
            <ClientCardHeader 
              clientName={clientName} 
              company={clientDetails.company}
              clientImage={clientImage}
              initials={initials}
              avatarColor={avatarColor}
              activityStatus={activityStatus}
              isShared={false}
            />
          </div>
          <div className="flex flex-row sm:flex-col items-center gap-2 mb-2 sm:mb-0">
            <ClientStatusBadge 
              status={activityStatus.status} 
              text={activityStatus.text} 
              isShared={isShared}
            />
            {relationship.status === 'inactive' ? (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="lg"
                      onClick={handleReactivate}
                      className="text-green-600 hover:text-green-700 hover:bg-green-50 p-2"
                    >
                      <UserPlus className="h-6 w-6" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="z-[99999] bg-white backdrop-blur-sm border border-[#CCCCCC] p-3" side="left">
                    <p>Click to reactivate connection with this client</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="lg"
                      onClick={() => setIsDisconnectDialogOpen(true)}
                      className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 p-2"
                    >
                      <UserMinus className="h-6 w-6" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="z-[99999] bg-white backdrop-blur-sm border border-[#CCCCCC] p-3" side="left">
                    <p>Disconnect from this client. You will no longer have visibility of their Journal</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className={relationship.status === 'inactive' ? 'pt-3 opacity-50 grayscale hover:grayscale-0 hover:opacity-75' : 'pt-3'}>
        <div className="flex flex-col gap-2">
          <p className="text-xs text-[#5A636E]">Connected since: {formatDate(relationship.created_at)}</p>
          <ClientCardActions 
            relationship={relationship}
            onClientSelect={onClientSelect}
            getClientDisplayName={getClientDisplayName}
            formatDate={formatDate}
            onRelationshipUpdate={onRelationshipUpdate}
          />
        </div>
      </CardContent>

      <DisconnectClientDialog
        open={isDisconnectDialogOpen}
        onOpenChange={setIsDisconnectDialogOpen}
        onConfirm={handleMakeInactive}
        clientName={clientName}
      />
    </Card>
  );
};

export default ClientCard;
